import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import AppDatePicker from "../../shared/AppDatePicker";
import ContextTooltip from "../../shared/ContextTooltip";
import { toDateInputValue } from "./utils";
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
} from "../../shared/customerSelectUtils";
import Autocomplete from "@mui/material/Autocomplete";

const IncomeUploadDialog = ({
    open,
    onClose,
    manual,
    onManualToggle,
    dropRef,
    fileInputRef,
    file,
    onFileChange,
    previewVisible,
    onPreviewToggle,
    previewUrl,
    isPreviewPdf,
    isPreviewImage,
    uploading,
    saving,
    incomeData,
    incomeErrors,
    onUpdateIncomeData,
    onClearIncomeError,
    customerOptions,
    loadingCustomers,
    incomeCustomerOption,
    onSave,
    onUpload,
    formatBytes,
    extOf,
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
            Einnahme erfassen
            <IconButton
                aria-label="Schliessen"
                onClick={onClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
            >
                <CloseRoundedIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Stack spacing={2}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={manual}
                            onChange={(e) => onManualToggle(e.target.checked)}
                        />
                    }
                    label="Manuell erfassen (ohne Datei)"
                />

                {!manual && (
                    <>
                        <Box
                            ref={dropRef}
                            sx={{
                                border: "2px dashed",
                                borderColor: "divider",
                                borderRadius: 3,
                                p: 4,
                                textAlign: "center",
                                transition: "border-color .12s ease, background-color .12s ease",
                                "&.drag-active": { borderColor: "orange", backgroundColor: "rgba(255,165,0,0.06)" },
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <CloudUploadRoundedIcon fontSize="large" />
                            <Typography variant="h6" sx={{ mt: 1, fontWeight: 800 }}>
                                Datei hierher ziehen
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                                oder <b>Klicken</b>, um eine Datei auszuwahlen
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: "none" }}
                                onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                            />
                        </Box>

                        {file && (
                            <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <InsertDriveFileRoundedIcon />
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                                            {file.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.75 }}>
                                            {formatBytes(file.size)}
                                        </Typography>
                                    </Box>
                                    <Chip size="small" label={extOf(file.name)} />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={onPreviewToggle}
                                        disabled={!file}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        {previewVisible ? "Beleg ausblenden" : "Beleg anzeigen"}
                                    </Button>
                                    <IconButton onClick={() => onFileChange(null)} disabled={uploading}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        )}
                    </>
                )}

                {file && !manual && (
                    <Collapse in={previewVisible}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Belegvorschau
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                                    {file.name} - {formatBytes(file.size)}
                                </Typography>
                                {previewUrl ? (
                                    <>
                                        {isPreviewPdf && (
                                            <Box sx={{ height: { xs: "50vh", md: "60vh" } }}>
                                                <iframe
                                                    title="Belegvorschau"
                                                    src={previewUrl}
                                                    style={{ width: "100%", height: "100%", border: "none" }}
                                                />
                                            </Box>
                                        )}
                                        {isPreviewImage && (
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Box
                                                    component="img"
                                                    src={previewUrl}
                                                    alt="Belegvorschau"
                                                    sx={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 2 }}
                                                />
                                            </Box>
                                        )}
                                        {!isPreviewPdf && !isPreviewImage && (
                                            <Alert severity="info" variant="outlined">
                                                Vorschau fuer diesen Dateityp ist nicht verfuegbar.
                                            </Alert>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Vorschau wird geladen.
                                    </Typography>
                                )}
                            </Stack>
                        </Paper>
                    </Collapse>
                )}

                {manual && (
                    <Stack spacing={3}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Belegdaten
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 item xs={12} sm={6}>
                                    <AppDatePicker
                                        label="Belegdatum"
                                        value={toDateInputValue(incomeData?.invoiceDate)}
                                        onChange={(date) => {
                                            onUpdateIncomeData({ invoiceDate: date });
                                            onClearIncomeError("invoiceDate");
                                        }}
                                        error={Boolean(incomeErrors.invoiceDate)}
                                        helperText={incomeErrors.invoiceDate || ""}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 item xs={12} sm={6}>
                                    <TextField
                                        label="Belegnummer/Anzeigename"
                                        value={incomeData?.invoiceNumber || ""}
                                        onChange={(e) => {
                                            onUpdateIncomeData({ invoiceNumber: e.target.value });
                                            onClearIncomeError("invoiceNumber");
                                        }}
                                        error={Boolean(incomeErrors.invoiceNumber)}
                                        helperText={incomeErrors.invoiceNumber || ""}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 item xs={12} sm={12}>
                                    <Autocomplete
                                        options={customerOptions}
                                        loading={loadingCustomers}
                                        value={incomeCustomerOption}
                                        onChange={(_, value) => {
                                            onUpdateIncomeData({
                                                customerId: value?.id || "",
                                                customerName: value?.displayName || value?.label || "",
                                            });
                                            onClearIncomeError("customerName");
                                        }}
                                        getOptionLabel={getCustomerOptionLabel}
                                        isOptionEqualToValue={isCustomerOptionEqualToValue}
                                        loadingText={CUSTOMER_SELECT_LOADING}
                                        noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={CUSTOMER_SELECT_LABEL}
                                                placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                error={Boolean(incomeErrors.customerName)}
                                                helperText={
                                                    incomeErrors.customerName ||
                                                    (!loadingCustomers && customerOptions.length === 0
                                                        ? CUSTOMER_SELECT_EMPTY_HELPER
                                                        : "")
                                                }
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCustomers ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        fullWidth
                                        sx={{ minWidth: 240 }}
                                    />
                                </Grid2>
                                <Grid2 item xs={12}>
                                    <TextField
                                        label="Beschreibung"
                                        value={incomeData?.description || ""}
                                        onChange={(e) => {
                                            onUpdateIncomeData({ description: e.target.value });
                                            onClearIncomeError("description");
                                        }}
                                        error={Boolean(incomeErrors.description)}
                                        helperText={incomeErrors.description || ""}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>

                        <Divider />

                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Betraege & Steuer
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 item xs={12} sm={4}>
                                    <TextField
                                        label="Betrag brutto"
                                        type="number"
                                        value={incomeData?.grossAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateIncomeData({ grossAmount: e.target.value }, "grossAmount");
                                            onClearIncomeError("grossAmount");
                                            onClearIncomeError("netAmount");
                                        }}
                                        error={Boolean(incomeErrors.grossAmount)}
                                        helperText={incomeErrors.grossAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 item xs={12} sm={4}>
                                    <TextField
                                        label="Betrag netto"
                                        type="number"
                                        value={incomeData?.netAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateIncomeData({ netAmount: e.target.value }, "netAmount");
                                            onClearIncomeError("netAmount");
                                        }}
                                        error={Boolean(incomeErrors.netAmount)}
                                        helperText={incomeErrors.netAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 item xs={12} sm={4}>
                                    <TextField
                                        label="USt-Betrag"
                                        type="number"
                                        value={incomeData?.vatAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateIncomeData({ vatAmount: e.target.value }, "vatAmount");
                                            onClearIncomeError("vatAmount");
                                            onClearIncomeError("netAmount");
                                        }}
                                        error={Boolean(incomeErrors.vatAmount)}
                                        helperText={incomeErrors.vatAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(incomeData?.taxExcludedContribution)}
                                                onChange={(e) =>
                                                    onUpdateIncomeData({ taxExcludedContribution: e.target.checked })
                                                }
                                            />
                                        }
                                        label="Als steuerlich nicht zu beruecksichtigende Einlage behandeln"
                                    />
                                </Grid2>
                                <Grid2 item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(incomeData?.isEuService)}
                                                onChange={(e) => {
                                                    onUpdateIncomeData({ isEuService: e.target.checked }, "isEuService");
                                                    onClearIncomeError("netAmount");
                                                }}
                                            />
                                        }
                                        label={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <span>Leistung an EU / EU-Ausland (Reverse Charge)</span>
                                                <ContextTooltip
                                                    title="EU-Leistung"
                                                    description="Bei EU-Leistungen wird die Einnahme netto als Betriebseinnahme erfasst. USt wird in der USt-Auswertung beruecksichtigt."
                                                />
                                            </Stack>
                                        }
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={uploading || saving}>
                Abbrechen
            </Button>
            {manual ? (
                <Button
                    onClick={onSave}
                    disabled={saving}
                    variant="contained"
                    sx={{ backgroundColor: "orange", color: "#000", fontWeight: 700, "&:hover": { backgroundColor: "#ff9e1a" } }}
                >
                    {saving ? "Speichere..." : "Speichern"}
                </Button>
            ) : (
                <Button
                    onClick={onUpload}
                    disabled={!file || uploading}
                    startIcon={<CloudUploadRoundedIcon />}
                    sx={{ backgroundColor: "orange", color: "#000", fontWeight: 700, "&:hover": { backgroundColor: "#ff9e1a" } }}
                    variant="contained"
                >
                    {uploading ? "Lade hoch..." : "Hochladen"}
                </Button>
            )}
        </DialogActions>
    </Dialog>
);

export default IncomeUploadDialog;
